import axios, { AxiosResponse } from 'axios';
import {
  FieldValues,
  UseFormSetError,
  FieldErrors,
  SubmitErrorHandler,
} from 'react-hook-form';
import { AppThunk } from '../../app/store';
import {
  setError,
  setLiveContentStatus,
  setLiveListStatus,
  setLiveSaveButtonStatus,
} from '../../features/uiSlice';
import { InputData } from '../../props/LiveRegister';
import { GetContract } from '../../types/API';
import { ToSetProfileStatusJap } from '../../utils/ChangeStatus';
import { setContract } from '../../features/userSlice';
import { EDITIONS, UI_STATUS, STANDBY_TYPE_ENG } from '../../utils/Const';
import {
  LIVE_UPDATE_SAVE_FLOW_ERR_MSG,
  LIVE_SAVE_STANDBY_TYPE_ERR_MSG,
  LIVE_SAVE_MAX_CLIENT_ERR_MSG,
} from '../../utils/Message';
import setInputParams from '../../utils/LiveSaveUtil';
import { ContentBaseSetting } from '../../props/LiveContent';

/** ライブプロファイル更新処理フロー */
const LiveUpdate =
  (
    data: InputData,
    edition: EDITIONS,
    encryptEdition: boolean,
    setSaveError: UseFormSetError<FieldValues>,
    onError: SubmitErrorHandler<InputData>,
    errors: FieldErrors<InputData>,
    baseSetting: ContentBaseSetting,
    liveStatusPre: boolean
  ): AppThunk =>
  async (dispatch, getState) => {
    const { user } = getState();
    let lpid = 0;
    // 動作モード設定 manualでない場合、契約会社情報取得API実行
    if (data.BaseSettingStandbyType !== STANDBY_TYPE_ENG.MANUAL) {
      dispatch(setLiveSaveButtonStatus(UI_STATUS.SAVING));
      // 契約会社情報取得API実行
      const responseGetContract: AxiosResponse<GetContract> = await axios.post(
        `${String(process.env.REACT_APP_BACKEND_API_ENDPOINT)}getContract`,
        {
          cid: user.contract.cid,
          userApiParams: user.user,
        },
        {
          headers: {
            accept: 'application/json',
            'Content-type': 'application/json',
          },
        }
      );
      dispatch(setContract({ ...responseGetContract.data }));
      let errorText = '';
      // 同時接続数チェック
      const maxClient =
        edition === EDITIONS.U_LIVE ? '0' : String(data.BaseSettingMaxClient);
      let contractNowClientCnt = responseGetContract.data.liveInfo.nowClientCnt;
      if (baseSetting.standbyType !== STANDBY_TYPE_ENG.MANUAL) {
        contractNowClientCnt -= baseSetting.maxClientLimit;
      }
      if (
        contractNowClientCnt + Number(maxClient) >
        responseGetContract.data.liveInfo.maxClient
      ) {
        errorText = LIVE_SAVE_MAX_CLIENT_ERR_MSG;
        errors.BaseSettingMaxClient = {
          type: 'required',
          message: errorText,
        };
        setSaveError('BaseSettingMaxClient', errors.BaseSettingMaxClient);
      }
      // 配信開催数チェック
      if (
        responseGetContract.data.liveInfo.nowCnt + 1 >
          responseGetContract.data.liveInfo.cntLive &&
        baseSetting.standbyType === STANDBY_TYPE_ENG.MANUAL
      ) {
        errorText = LIVE_SAVE_STANDBY_TYPE_ERR_MSG;
        errors.BaseSettingStandbyType = {
          type: 'required',
          message: errorText,
        };
        setSaveError('BaseSettingStandbyType', errors.BaseSettingStandbyType);
      }
      if (errorText !== '') {
        dispatch(setLiveSaveButtonStatus(UI_STATUS.PROCESSING));
        onError(errors);
        return false;
      }
    }
    // 処理中をセット
    dispatch(setLiveContentStatus(UI_STATUS.SAVING));
    try {
      // ライブ設定IDとJCloud wlives サーバーIDを取得
      const searchString = window.location.search;
      const params = new URLSearchParams(searchString);
      lpid = Number(params.get('lpid'));

      // 画面入力項目のパラメータ設定
      let formData;
      // 配信準備中の場合は同時接続数制限のみをパラメータに設定する
      if (liveStatusPre) {
        formData = new FormData();
        formData.append(
          'max_client',
          edition === EDITIONS.U_LIVE ? '0' : String(data.BaseSettingMaxClient)
        );
      } else {
        formData = setInputParams(data, edition, encryptEdition);
      }
      // 契約会社ID
      formData.append('cid', String(user.contract.cid));
      // ライブ設定ID
      formData.append('lpid', String(lpid));
      // userApiParams
      formData.append('userApiParams', user.user);

      // ライブプロファイル更新
      const responseSetLiveProfile = await axios.post(
        `${String(
          process.env.REACT_APP_BACKEND_API_ENDPOINT
        )}setProfileProcess`,
        formData,
        {
          headers: {
            accept: 'application/json',
            'Content-type': 'multipart/form-data',
          },
        }
      );
      if (responseSetLiveProfile.data.errorCode !== 0) {
        // 異常終了
        // NOTE: 400系のクライアントサイドのエラーだが、エラー画面に遷移させないとチェックボックスの不具合が生じるため、例外的にエラー画面に遷移
        throw new Error(
          `${ToSetProfileStatusJap(responseSetLiveProfile.data.errorCode)}: ${
            responseSetLiveProfile.data.message
          }`
        );
      }

      // 正常終了
      dispatch(setLiveContentStatus(UI_STATUS.REDIRECT));
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // 異常終了
      dispatch(
        setError({
          msg: LIVE_UPDATE_SAVE_FLOW_ERR_MSG,
          detail: JSON.stringify(error.response?.data || error?.message),
        })
      );
      dispatch(setLiveContentStatus(UI_STATUS.COMPLETED));
      return false;
    } finally {
      dispatch(setLiveListStatus(UI_STATUS.PROCESSING));
      dispatch(setLiveSaveButtonStatus(UI_STATUS.PROCESSING));
    }
  };

export default LiveUpdate;
